import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { PageHeader } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Download, Printer, ChevronLeft, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import { ReportDateFilter, useReportRange } from "../components/ReportDateFilter";

const num = (n) => Number(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

function downloadCsv(name, head, rows) {
  const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "")}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `${name}.csv`; a.click();
}

export default function StockMovementReport({ report, onBack }) {
  const { branchParam, selected, branches } = useBranch();
  const branchLabel = (selected && selected !== "all") ? (branches.find((b) => b.id === selected)?.name || "Your Branch") : "All Branches";
  const rr = useReportRange("month");
  const { start, end } = rr;
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/inventory/movement-report", { params: { start, end, branch_id: branchParam } }).then((r) => setData(r.data));
  }, [start, end, branchParam]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const t = data?.totals || {};

  const csvExport = () => downloadCsv("stock_movement_report",
    ["Product", "Type", "Branch", "Unit", "Opening", "Inward", "Consumption", "Sales", "Transfers", "Adjustments", "Closing", "Min", "Reorder"],
    rows.map((r) => [r.product_name, r.product_type, r.branch_name, r.unit, r.opening, r.inward, r.consumption, r.sales, r.transfers, r.adjustments, r.closing, r.min_stock, r.reorder ? "YES" : ""]));

  const pdfExport = async () => {
    try {
      const res = await api.get("/inventory/movement-report.pdf", { params: { start, end, branch_id: branchParam }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "stock_movement_report.pdf";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("PDF export failed"); }
  };

  const suggestReorder = (r) => toast.info(`Reorder suggestion for ${r.product_name} at ${r.branch_name}: order ~${num(r.reorder_qty)} ${r.unit} (current ${num(r.closing)}, min ${num(r.min_stock)})`, { duration: 6000 });

  const th = "text-muted-foreground text-left border-b border-border";
  return (
    <div>
      <button onClick={onBack} data-testid="stock-move-back" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"><ChevronLeft size={16} /> All Reports</button>
      <PageHeader title="Product Movement Report" subtitle="Branch-wise stock movement from the inventory ledger — opening, inward, consumption, sales, transfers, adjustments, closing"
        actions={
          <div className="flex gap-2">
            <Button onClick={csvExport} variant="outline" className="rounded-sm" data-testid="stock-move-csv"><Download size={15} className="mr-1" /> Excel/CSV</Button>
            <Button onClick={pdfExport} variant="outline" className="rounded-sm" data-testid="stock-move-pdf"><Download size={15} className="mr-1" /> PDF</Button>
            <Button onClick={() => window.print()} variant="outline" className="rounded-sm" data-testid="stock-move-print"><Printer size={15} className="mr-1" /> Print</Button>
          </div>
        } />

      <Card className="p-4 bg-card border-border mb-6">
        <Badge variant="outline" className="border-primary text-primary mb-3" data-testid="stock-move-branch-chip">Showing: {branchLabel}</Badge>
        <ReportDateFilter r={rr} prefix="stock-move" />
      </Card>

      <Card className="bg-card border-border overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead><tr className={th}>
            <th className="py-2 px-3">Product</th><th className="px-3">Branch</th><th className="px-3">Unit</th>
            <th className="text-right px-3">Opening</th><th className="text-right px-3">Inward</th>
            <th className="text-right px-3">Consumption</th><th className="text-right px-3">Sales</th>
            <th className="text-right px-3">Transfers</th><th className="text-right px-3">Adjustments</th>
            <th className="text-right px-3">Closing</th><th className="text-center px-3">Reorder</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.product_id + r.branch_id} className="border-b border-border/50" data-testid={`stock-move-row-${r.product_id}-${r.branch_id}`}>
                <td className="py-2 px-3">{r.product_name} <span className="text-xs text-muted-foreground">{r.product_type === "sell" ? "(Sale)" : "(Use)"}</span></td>
                <td className="px-3 text-muted-foreground">{r.branch_name}</td>
                <td className="px-3 text-muted-foreground">{r.unit}</td>
                <td className="text-right px-3">{num(r.opening)}</td>
                <td className="text-right px-3 text-green-400">{num(r.inward)}</td>
                <td className="text-right px-3 text-amber-400">{num(r.consumption)}</td>
                <td className="text-right px-3 text-amber-400">{num(r.sales)}</td>
                <td className="text-right px-3">{num(r.transfers)}</td>
                <td className="text-right px-3">{num(r.adjustments)}</td>
                <td className="text-right px-3 text-primary font-medium">{num(r.closing)}</td>
                <td className="text-center px-3">
                  {r.reorder ? (
                    <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm border-red-800 text-red-300 hover:bg-red-900/30"
                      onClick={() => suggestReorder(r)} data-testid={`reorder-${r.product_id}-${r.branch_id}`}>
                      <PackagePlus size={13} className="mr-1" /> Reorder
                    </Button>
                  ) : <span className="text-muted-foreground text-xs">—</span>}
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={11} className="text-center text-muted-foreground py-10">No stock movement in this range.</td></tr>}
          </tbody>
          {rows.length > 0 && (
            <tfoot><tr className="border-t border-border font-medium">
              <td className="py-2 px-3" colSpan={3}>TOTAL ({t.count} rows)</td>
              <td className="text-right px-3">{num(t.opening)}</td>
              <td className="text-right px-3 text-green-400">{num(t.inward)}</td>
              <td className="text-right px-3 text-amber-400">{num(t.consumption)}</td>
              <td className="text-right px-3 text-amber-400">{num(t.sales)}</td>
              <td className="text-right px-3">{num(t.transfers)}</td>
              <td className="text-right px-3">{num(t.adjustments)}</td>
              <td className="text-right px-3 text-primary">{num(t.closing)}</td>
              <td className="text-center px-3 text-red-300">{t.reorder_count || 0}</td>
            </tr></tfoot>
          )}
        </table>
      </Card>
    </div>
  );
}
